import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { MdBlock, MdInline } from '@/lib/markdown';
import { parseMarkdown } from '@/lib/markdown';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { normalizeWikiTitle } from '@/lib/wikiSlug';
import { cn } from '@/lib/utils';

interface MarkdownContentProps {
  /** Raw Markdown source (a wiki article's or publication section's `content` field, when it turns out to be plain Markdown rather than Djot/AsciiDoc). */
  content: string;
  className?: string;
}

/**
 * Renders plain Markdown content — used when `@/lib/contentFormat`
 * determines that a wiki article or publication section is written in
 * ordinary Markdown rather than NIP-54's Djot or NKBIP-01's AsciiDoc.
 * This is common in practice: Nostr is permissionless, and most authors
 * default to Markdown regardless of what a given kind's spec recommends.
 *
 * Security: this NEVER uses `dangerouslySetInnerHTML`. Every node maps to
 * a concrete React element with auto-escaped text. Raw inline/block HTML
 * (technically valid in CommonMark) is NOT implemented by the parser —
 * there's no HTML string produced for it to leak. Every link/image
 * destination goes through `sanitizeUrl()`.
 */
export function MarkdownContent({ content, className }: MarkdownContentProps) {
  const doc = parseMarkdown(content);

  return (
    <div className={cn('break-words', className)}>
      {doc.children.map((block, i) => (
        <Fragment key={i}>{renderBlock(block)}</Fragment>
      ))}
    </div>
  );
}

function renderBlocks(blocks: MdBlock[]): ReactNode {
  return blocks.map((block, i) => <Fragment key={i}>{renderBlock(block)}</Fragment>);
}

function renderBlock(block: MdBlock): ReactNode {
  switch (block.tag) {
    case 'heading': {
      const level = Math.min(Math.max(block.level, 1), 6);
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      const sizes: Record<number, string> = {
        1: 'text-3xl font-bold mt-8 mb-4',
        2: 'text-2xl font-bold mt-8 mb-3 border-b pb-2',
        3: 'text-xl font-semibold mt-6 mb-3',
        4: 'text-lg font-semibold mt-5 mb-2',
        5: 'text-base font-semibold mt-4 mb-2',
        6: 'text-sm font-semibold mt-4 mb-2 text-muted-foreground',
      };
      return (
        <Tag id={block.id} className={sizes[level]}>
          {renderInlines(block.children)}
        </Tag>
      );
    }

    case 'paragraph':
      return <p className="mb-4 leading-7">{renderInlines(block.children)}</p>;

    case 'thematic_break':
      return <hr className="my-6 border-border" />;

    case 'blockquote':
      return (
        <blockquote className="mb-4 border-l-4 border-border pl-4 italic text-muted-foreground [&>*:last-child]:mb-0">
          {renderBlocks(block.children)}
        </blockquote>
      );

    case 'code':
      return (
        <pre className="mb-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm">
          <code className={block.lang ? `language-${block.lang.replace(/[^\w-]/g, '')}` : undefined}>
            {block.text}
          </code>
        </pre>
      );

    case 'bullet_list':
      return (
        <ul className="mb-4 list-disc space-y-1 pl-6">
          {block.items.map((item, i) => (
            <li key={i}>
              {renderInlines(item.children)}
              {item.sublist && renderBlock(item.sublist)}
            </li>
          ))}
        </ul>
      );

    case 'ordered_list':
      return (
        <ol className="mb-4 list-decimal space-y-1 pl-6" start={block.start !== 1 ? block.start : undefined}>
          {block.items.map((item, i) => (
            <li key={i}>
              {renderInlines(item.children)}
              {item.sublist && renderBlock(item.sublist)}
            </li>
          ))}
        </ol>
      );

    case 'table':
      return (
        <div className="mb-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className="border-b border-border">
                  {row.map((cell, j) => {
                    const align = block.alignments[j];
                    const style = align ? { textAlign: align } : undefined;
                    return i === 0 ? (
                      <th key={j} style={style} className="p-2 text-left font-semibold">
                        {renderInlines(cell)}
                      </th>
                    ) : (
                      <td key={j} style={style} className="p-2">
                        {renderInlines(cell)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return null;
  }
}

function renderInlines(nodes: MdInline[]): ReactNode {
  return nodes.map((node, i) => <Fragment key={i}>{renderInline(node)}</Fragment>);
}

function renderInline(node: MdInline): ReactNode {
  switch (node.tag) {
    case 'text':
      return node.text;

    case 'break':
      return <br />;

    case 'strong':
      return <strong>{renderInlines(node.children)}</strong>;

    case 'emphasis':
      return <em>{renderInlines(node.children)}</em>;

    case 'code':
      return <code className="rounded bg-muted px-1.5 py-0.5 text-sm">{node.text}</code>;

    case 'link': {
      const href = sanitizeUrl(node.href);
      const children = renderInlines(node.children);
      return href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:no-underline">
          {children}
        </a>
      ) : (
        <>{children}</>
      );
    }

    case 'image': {
      const src = sanitizeUrl(node.src);
      if (!src) return node.alt ? <span className="text-muted-foreground">[{node.alt}]</span> : null;
      return <img src={src} alt={node.alt} className="mx-auto my-4 max-w-full rounded-lg" loading="lazy" />;
    }

    case 'wikilink': {
      const slug = normalizeWikiTitle(node.target);
      const label = node.display || node.target;
      if (!slug) return <>{label}</>;
      return (
        <Link to={`/w/${encodeURIComponent(slug)}`} className="text-primary underline decoration-dotted underline-offset-2 hover:no-underline">
          {label}
        </Link>
      );
    }

    default:
      return null;
  }
}
